'use client';

import { FC, useState, useCallback } from 'react';
import { Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { COLORS, PATHS } from '@/constants';
import { normalizeHTML } from '@/common';
import { useGetProjectObjects } from '@/queris';
import { clearListingNavState } from '@/hooks';
import { ContentCard } from '@/features/pages/housing-detail/styles';
import {
  Table,
  TableHead,
  TableRowLink,
  TableRowStatic,
  ShowMoreButton,
  RowThumb,
  RowTitleCell,
  RowMobileStats,
  RowMobileThumb,
  RowArrow,
  ProjectTitleLink,
} from './styled';
import { formatProjectCardPrice } from '../../utils';
import { ArrowRight } from '@/components';
import { GalleryThumbImage } from '@/components/shared/GalleryThumbImage/GalleryThumbImage';

const INITIAL_ROWS = 10;

type ProjectObjectsProps = {
  projectId: number | null | undefined;
  totalCount: number | null;
  projectSlug?: string;
  projectName?: string;
  excludeId?: string;
};

export const ProjectObjects: FC<ProjectObjectsProps> = ({
  projectId,
  totalCount,
  projectSlug,
  projectName,
  excludeId,
}) => {
  const t = useTranslations();
  const { data, isLoading } = useGetProjectObjects(projectId);
  const [showAll, setShowAll] = useState(false);
  const handleObjectClick = useCallback(() => clearListingNavState(), []);

  const objects = data?.objects ?? [];
  const numFound = data?.numFound ?? totalCount ?? 0;
  const displayed = showAll ? objects : objects.slice(0, INITIAL_ROWS);

  if (!projectId || (isLoading && !objects.length)) return null;
  if (!objects.length) return null;

  return (
    <ContentCard>
      <Typography fontWeight={600} fontSize={20} lineHeight="28px">
        {projectName && projectSlug
          ? (() => {
              const template =
                numFound === 1
                  ? t('projectDetails.unitsTitleLinkedSingular', { name: '\x00' })
                  : t('projectDetails.unitsTitleLinked', { name: '\x00', count: numFound });
              const [before, after] = template.split('\x00');
              return (
                <>
                  {before}
                  <ProjectTitleLink href={`/${PATHS.PROJECT}/${projectSlug}`}>
                    {projectName}
                  </ProjectTitleLink>
                  {after}
                </>
              );
            })()
          : t('projectDetails.objectsTitle', { shown: displayed.length, total: numFound })}
      </Typography>

      <Table>
        <TableHead>
          <Typography fontSize={13} fontWeight={700} color={COLORS['primary/910']}>
            {t('projectDetails.tableTitle')}
          </Typography>
          <Typography
            fontSize={13}
            fontWeight={700}
            color={COLORS['primary/910']}
            textAlign="center"
          >
            {t('projectDetails.tableLivingArea')}
          </Typography>
          <Typography
            fontSize={13}
            fontWeight={700}
            color={COLORS['primary/910']}
            textAlign="center"
          >
            {t('projectDetails.tableRooms')}
          </Typography>
          <Typography fontSize={13} fontWeight={700} color={COLORS['primary/910']}>
            {t('projectDetails.tablePrice')}
          </Typography>
          <span />
        </TableHead>

        {displayed.map((item) => {
          const slugLink = `${item.slug}__${item.id}`;
          const href = `/${PATHS.HOUSING}/${slugLink}`;
          const thumbSrc =
            item.imageList?.[0]?.imageTmbUrl ||
            item.imageList?.[0]?.imageFullUrl ||
            item.imageFullUrl ||
            null;
          const isCurrent = item.id === excludeId;
          const rowContent = (
            <>
              {/* Desktop: thumbnail + title in one cell */}
              <RowTitleCell>
                <RowThumb>
                  <GalleryThumbImage src={thumbSrc} />
                </RowThumb>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color={COLORS['primary/910']}
                  noWrap
                  className="row-title"
                >
                  {normalizeHTML(item.title)}
                </Typography>
              </RowTitleCell>
              <Typography
                fontSize={14}
                fontWeight={500}
                color={COLORS['primary/910']}
                sx={{ textAlign: 'center', '@media (max-width: 640px)': { display: 'none' } }}
              >
                {item.squares?.areaDetailed || '–'}
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={500}
                color={COLORS['primary/910']}
                noWrap
                sx={{ textAlign: 'center', '@media (max-width: 640px)': { display: 'none' } }}
              >
                {item.otherDetails?.roomNumber || '–'}
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={500}
                color={COLORS['primary/910']}
                noWrap
                sx={{ '@media (max-width: 640px)': { display: 'none' } }}
              >
                {formatProjectCardPrice(item, t('housingCardDetails.monthly'))}
              </Typography>
              {/* Desktop last col / Mobile col 3: arrow */}
              {isCurrent ? (
                <span />
              ) : (
                <RowArrow>
                  <ArrowRight
                    width={12}
                    height={12}
                    stroke={COLORS['secondary/500']}
                    fill={COLORS['secondary/500']}
                  />
                </RowArrow>
              )}
              {/* Mobile only: thumbnail (col 1) + stats (col 2) */}
              <RowMobileThumb>
                <GalleryThumbImage src={thumbSrc} />
              </RowMobileThumb>
              <RowMobileStats>
                <Typography fontSize={14} fontWeight={600} color={COLORS['primary/910']} noWrap>
                  {normalizeHTML(item.title)}
                </Typography>
                <Typography fontSize={13} color={COLORS['neutral/600']} noWrap>
                  {[
                    formatProjectCardPrice(item, t('housingCardDetails.monthly')),
                    item.squares?.areaDetailed,
                    item.otherDetails?.roomNumber
                      ? `${item.otherDetails.roomNumber} ${t('shortRooms')}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </RowMobileStats>
            </>
          );

          return isCurrent ? (
            <TableRowStatic key={item.id}>{rowContent}</TableRowStatic>
          ) : (
            <TableRowLink key={item.id} href={href} onClick={handleObjectClick}>
              {rowContent}
            </TableRowLink>
          );
        })}

        {!showAll && objects.length > INITIAL_ROWS && (
          <ShowMoreButton onClick={() => setShowAll(true)}>
            {t('projectDetails.showMore', { count: objects.length - INITIAL_ROWS })}
          </ShowMoreButton>
        )}
      </Table>
    </ContentCard>
  );
};
